import React, { useContext, useState } from 'react';
import { AuthContext } from '../../../../AuthContext';
import { db } from '../../../../firebase';
import { collection, query, where, getDocs, limit } from 'firebase/firestore';
import './UserLookupTab.css';

const formatTimestamp = (value) => {
    if (!value) return null;
    const date = value.toDate ? value.toDate() : new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
};

const monthsSince = (value) => {
    if (!value) return null;
    const start = value.toDate ? value.toDate() : new Date(value);
    if (Number.isNaN(start.getTime())) return null;
    const now = new Date();
    let months = (now.getFullYear() - start.getFullYear()) * 12 + (now.getMonth() - start.getMonth());
    if (now.getDate() < start.getDate()) months -= 1;
    return Math.max(months, 0);
};

const UserLookupTab = () => {
    const { isAdmin } = useContext(AuthContext);
    const [emailInput, setEmailInput] = useState('');
    const [searching, setSearching] = useState(false);
    const [error, setError] = useState(null);
    const [foundUser, setFoundUser] = useState(null);
    const [purchasedVideos, setPurchasedVideos] = useState([]);
    const [purchasedPhotos, setPurchasedPhotos] = useState([]);

    if (!isAdmin) {
        return (
            <div className="user-lookup-tab">
                <h3>User Lookup</h3>
                <p className="user-lookup-tab__notice">Admin access required.</p>
            </div>
        );
    }

    const findUserByEmail = async (email) => {
        const usersRef = collection(db, 'users');
        // Email lives at userProfile.userEmail (there is no top-level email field).
        // Try the value as typed first, then fall back to lowercase in case it
        // was normalized at signup.
        for (const candidate of [email, email.toLowerCase()]) {
            const q = query(usersRef, where('userProfile.userEmail', '==', candidate), limit(1));
            const snapshot = await getDocs(q);
            if (!snapshot.empty) return snapshot.docs[0];
            if (candidate === email.toLowerCase()) break;
        }
        return null;
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        const email = emailInput.trim();
        if (!email) return;

        setSearching(true);
        setError(null);
        setFoundUser(null);
        setPurchasedVideos([]);
        setPurchasedPhotos([]);

        try {
            const userDoc = await findUserByEmail(email);

            if (!userDoc) {
                setError('No user found with that email.');
                setSearching(false);
                return;
            }

            setFoundUser({ id: userDoc.id, ...userDoc.data() });

            const [videosSnap, photosSnap] = await Promise.all([
                getDocs(collection(db, 'users', userDoc.id, 'purchased_video_links')),
                getDocs(collection(db, 'users', userDoc.id, 'customLinks'))
            ]);

            setPurchasedVideos(videosSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
            setPurchasedPhotos(photosSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
        } catch (err) {
            console.error('Error searching for user:', err);
            setError(err.message || 'Failed to search for user.');
        } finally {
            setSearching(false);
        }
    };

    const subscriptionMonths = foundUser ? monthsSince(foundUser.subscriptionStartedAt) : null;
    const displayName = foundUser?.userProfile?.username || foundUser?.userProfile?.userEmail || 'Unnamed User';
    const email = foundUser?.userProfile?.userEmail;

    return (
        <div className="user-lookup-tab">
            <h3>User Lookup</h3>

            <form className="user-lookup-tab__search" onSubmit={handleSearch}>
                <input
                    type="email"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder="user@example.com"
                    required
                />
                <button type="submit" disabled={searching}>
                    {searching ? 'Searching...' : 'Search'}
                </button>
            </form>

            {error && <p className="user-lookup-tab__error">{error}</p>}

            {foundUser && (
                <div className="user-lookup-tab__results">
                    <div className="user-lookup-tab__summary">
                        <h4>{displayName}</h4>
                        {email && <p className="user-lookup-tab__email">{email}</p>}

                        <div className="user-lookup-tab__badges">
                            <span className={`user-lookup-tab__badge user-lookup-tab__badge--${foundUser.subscriptionStatus || 'none'}`}>
                                {foundUser.subscriptionStatus || 'No subscription'}
                            </span>
                            {foundUser.role && (
                                <span className="user-lookup-tab__badge user-lookup-tab__badge--role">
                                    {foundUser.role}
                                </span>
                            )}
                        </div>

                        {subscriptionMonths !== null && (
                            <p>
                                Subscribed for {subscriptionMonths} {subscriptionMonths === 1 ? 'month' : 'months'}
                                {' '}(since {formatTimestamp(foundUser.subscriptionStartedAt)})
                            </p>
                        )}
                        {!foundUser.subscriptionStartedAt && foundUser.subscriptionStatus && (
                            <p className="user-lookup-tab__hint">
                                Subscribed before we started tracking start dates — exact tenure unknown.
                            </p>
                        )}
                        {foundUser.subscriptionStatus === 'pending_cancellation' && foundUser.subscriptionEndDateReadable && (
                            <p className="user-lookup-tab__warning">
                                Cancels on {foundUser.subscriptionEndDateReadable}
                            </p>
                        )}
                        {foundUser.createdAt && (
                            <p className="user-lookup-tab__meta">
                                Account created {formatTimestamp(foundUser.createdAt)}
                            </p>
                        )}
                    </div>

                    <div className="user-lookup-tab__section">
                        <h5>Purchased Videos ({purchasedVideos.length})</h5>
                        {purchasedVideos.length === 0 ? (
                            <p className="user-lookup-tab__empty">No video purchases.</p>
                        ) : (
                            <ul className="user-lookup-tab__video-list">
                                {purchasedVideos.map((video) => (
                                    <li key={video.id}>
                                        {video.thumbnail && (
                                            <img src={video.thumbnail} alt={video.title || 'Video thumbnail'} />
                                        )}
                                        <div className="user-lookup-tab__video-meta">
                                            <strong>{video.title || 'Untitled'}</strong>
                                            <span>{formatTimestamp(video.purchasedAt)}</span>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    <div className="user-lookup-tab__section">
                        <h5>Purchased Photos ({purchasedPhotos.length})</h5>
                        {purchasedPhotos.length === 0 ? (
                            <p className="user-lookup-tab__empty">No custom photo purchases.</p>
                        ) : (
                            <div className="user-lookup-tab__photo-grid">
                                {purchasedPhotos.map((photo) => (
                                    <a
                                        key={photo.id}
                                        href={photo.imageUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="user-lookup-tab__photo"
                                    >
                                        <img src={photo.imageUrl} alt={photo.outfitLabel || 'Purchased photo'} />
                                        <span>
                                            {[photo.outfitLabel, photo.poseLabel].filter(Boolean).join(' / ')
                                                || `Request #${photo.requestNumber || photo.id}`}
                                        </span>
                                        <span className="user-lookup-tab__photo-date">
                                            {formatTimestamp(photo.fulfilledAt || photo.createdAt)}
                                        </span>
                                    </a>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserLookupTab;
